/**
 * Phase BACKGROUND-ANALYSIS-P1a.5-vi — polls minimal outfit metadata
 * (status fields + LB url columns + input_method) for stuck detection,
 * strip-mount, and hero-variant spinner gating in Results.
 *
 * Terminal-success predicate is column-based (not lookbook_status):
 *   analysis_status === 'complete' AND
 *   lookbook_image_url IS NOT NULL AND
 *   lookbook_lb2_url IS NOT NULL AND
 *   (!expectsLb3 OR lookbook_lb3_url IS NOT NULL)
 *
 * `expectsLb3` reflects whether the orchestrator will dispatch LB3 for
 * this user (authed + non-empty wardrobe_items, unfiltered to match
 * analyse-outfit/index.ts:2890). Guests + empty-wardrobe authed users
 * only expect LB1 + LB2.
 *
 * Why polling instead of Realtime (do NOT "optimize" by removing this):
 *  1. The `outfits` table is NOT in the `supabase_realtime` publication.
 *  2. A dropped postgres_changes event on the terminal transition would
 *     strand UI forever — polling is the safety net.
 *  3. iOS WebView background→foreground is more predictable with polling
 *     + visibilitychange than long-lived Realtime channels.
 *
 * Hook auto-stops on terminal state; uses get_guest_outfit RPC for
 * guest sessions to bypass RLS.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface OutfitMeta {
  id: string;
  created_at: string;
  input_method: string | null;
  analysis_status: string | null;
  lookbook_status: string | null;
  image_url: string | null;
  lookbook_image_url: string | null;
  lookbook_lb2_url: string | null;
  lookbook_lb3_url: string | null;
  // LB3-DISPLAY-RECOVERY-1: source of truth for LB3 outcome + shopping list.
  lb3_outcome: string | null;
  lb3_owned_items: unknown;
  lb3_gap_items: unknown;
}

const SELECT_COLS =
  "id, created_at, input_method, analysis_status, lookbook_status, image_url, lookbook_image_url, lookbook_lb2_url, lookbook_lb3_url, lb3_outcome, lb3_owned_items, lb3_gap_items";

function isTerminal(row: OutfitMeta, expectsLb3: boolean): boolean {
  if (row.analysis_status === "failed") return true;
  if (row.analysis_status !== "complete") return false;
  const lb1 = !!row.lookbook_image_url;
  const lb2 = !!row.lookbook_lb2_url;
  const lb3 = !expectsLb3 || !!row.lookbook_lb3_url;
  return lb1 && lb2 && lb3;
}

export function useOutfitMetaPolling(
  outfitId: string | null,
  isGuest: boolean,
  expectsLb3: boolean,
): OutfitMeta | null {
  const [meta, setMeta] = useState<OutfitMeta | null>(null);

  useEffect(() => {
    if (!outfitId) {
      setMeta(null);
      return;
    }
    let cancelled = false;
    let intervalId: number | null = null;

    const fetchOnce = async () => {
      let row: OutfitMeta | null = null;
      if (isGuest) {
        const r = await (supabase.rpc as never as (
          fn: "get_guest_outfit",
          args: { p_id: string },
        ) => Promise<{ data: OutfitMeta[] | null; error: unknown }>)(
          "get_guest_outfit",
          { p_id: outfitId },
        );
        if (r.data && r.data.length > 0) row = r.data[0];
      } else {
        const r = await supabase
          .from("outfits")
          .select(SELECT_COLS)
          .eq("id", outfitId)
          .maybeSingle();
        if (r.data) row = r.data as OutfitMeta;
      }
      if (cancelled) return;
      if (row) setMeta(row);
      if (row && isTerminal(row, expectsLb3)) {
        if (intervalId !== null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
      }
    };

    void fetchOnce();
    intervalId = window.setInterval(() => {
      void fetchOnce();
    }, 5_000);

    return () => {
      cancelled = true;
      if (intervalId !== null) window.clearInterval(intervalId);
    };
  }, [outfitId, isGuest, expectsLb3]);

  return meta;
}
